import { useState, useEffect, useCallback, useRef } from "react";
import { UploadSimple, Trash, DownloadSimple, ShieldCheck, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const ClientAttachmentsDialog = ({ client, onClose }) => {
  const [files, setFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!client) return;
    try {
      const { data } = await api.get(`/clients/${client.id}/attachments`);
      setFiles(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [client]);

  useEffect(() => { setFiles(null); load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/clients/${client.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Anexo enviado com segurança");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/clients/${client.id}/attachments/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Excluir o anexo "${f.original_filename}"?`)) return;
    try {
      await api.delete(`/clients/${client.id}/attachments/${f.id}`);
      toast.success("Anexo excluído");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Dialog open={!!client} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Anexos — {client?.name}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Documentos e imagens do cliente (visível apenas para administradores)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4" data-testid="attachments-dialog">
          <div className="border border-emerald-400/20 bg-emerald-400/5 rounded p-3 flex items-start gap-2">
            <ShieldCheck size={16} className="text-emerald-400 mt-0.5 shrink-0" />
            <p className="text-[11px] text-zinc-400">
              Todo arquivo passa por verificação de segurança: extensões perigosas (.exe, .bat, .js...) são bloqueadas
              e a assinatura real do conteúdo é validada. Limite: 10 MB. Permitidos: PDF, imagens (PNG/JPG/GIF/WebP), DOCX, XLSX, CSV, TXT.
            </p>
          </div>
          <div>
            <input ref={inputRef} type="file" className="hidden" onChange={upload}
              accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.docx,.xlsx,.csv,.txt" data-testid="attachment-file-input" />
            <button data-testid="upload-attachment-button" disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded transition-colors">
              <UploadSimple size={15} weight="bold" /> {uploading ? "Verificando e enviando..." : "Enviar Arquivo"}
            </button>
          </div>
          <div className="border border-zinc-800 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-zinc-500 border-b border-zinc-800">
                  <th className="px-4 py-2.5 font-medium">Arquivo</th>
                  <th className="px-4 py-2.5 font-medium">Tamanho</th>
                  <th className="px-4 py-2.5 font-medium">Enviado em</th>
                  <th className="px-4 py-2.5 font-medium text-right"></th>
                </tr>
              </thead>
              <tbody>
                {files && files.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-500">Nenhum anexo ainda.</td></tr>
                )}
                {(files || []).map((f) => (
                  <tr key={f.id} data-testid={`attachment-row-${f.id}`} className="border-b border-zinc-800/60 hover:bg-zinc-800/40 transition-colors">
                    <td className="px-4 py-2.5 text-zinc-300">
                      <span className="flex items-center gap-2">
                        <FileText size={15} className="text-indigo-400 shrink-0" />
                        <span className="truncate max-w-[200px]" title={f.original_filename}>{f.original_filename}</span>
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-400 whitespace-nowrap">{fmtSize(f.size)}</td>
                    <td className="px-4 py-2.5 text-zinc-400 whitespace-nowrap">{fmtDate(f.created_at?.slice(0, 10))}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button data-testid={`download-attachment-${f.id}`} onClick={() => download(f)} title="Baixar"
                        className="text-zinc-500 hover:text-emerald-400 p-1 transition-colors"><DownloadSimple size={15} /></button>
                      <button data-testid={`delete-attachment-${f.id}`} onClick={() => remove(f)} title="Excluir"
                        className="text-zinc-500 hover:text-rose-400 p-1 transition-colors"><Trash size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
